import { useCallback, useEffect, useRef, useState } from 'react';

import { AgGridReact } from 'ag-grid-react';
import { useParams, useSearchParams } from 'react-router-dom';

import { WithLocalStorage } from '@/components';
import usePageTitle from '@/components/hooks/usePageTitle';

import { Grid } from './components/Grid';
import { type DateRangePreset, Toolbar } from './components/Toolbar';
import { type ExternalFilters, useAuditDatasource } from './util/datasource';
import { TransformedLog } from './util/types';

export function AuditLogPage() {
  const params = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL-backed filter state — search and date-range preset are shareable
  const search = searchParams.get('q') ?? '';
  const dateRange = (searchParams.get('range') ?? 'all') as DateRangePreset;

  const setSearch = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('q', value);
    else next.delete('q');
    setSearchParams(next, { replace: true });
  };
  const setDateRange = (value: DateRangePreset) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('range');
    else next.set('range', value);
    setSearchParams(next, { replace: true });
  };

  const grid = useRef<AgGridReact<TransformedLog>>(null);
  const [entityDisplayName, setEntityDisplayName] = useState<string>('');

  const onMetadata = useCallback((displayName: string | null) => {
    if (displayName) setEntityDisplayName(displayName);
  }, []);

  const { setTitle } = usePageTitle();
  useEffect(() => {
    const shortId = (params.id ?? '').split('-')[0];
    const entity = entityDisplayName || 'Record';
    const breadcrumb = params.id
      ? `${entity} ${shortId} · History`
      : 'Audit history';
    const browser = params.id
      ? `Audit · ${entity} ${shortId}`
      : 'Audit history';
    setTitle(breadcrumb, browser);
  }, [entityDisplayName, params.id, setTitle]);

  // Compute server-side filter values from toolbar state.
  const filters: ExternalFilters = {
    q: search,
    startDate: cutoffIsoFor(dateRange) ?? '',
    endDate: '',
  };

  const dataSource = useAuditDatasource({
    objectId: params.id,
    filters,
    onMetadata,
  });

  // Refresh server-side store whenever toolbar filters change so the grid
  // re-fetches page 0 with the new params.
  useEffect(() => {
    grid.current?.api?.refreshServerSide({ purge: true });
  }, [search, dateRange]);

  return (
    <WithLocalStorage storageKey="audit-log-grid">
      <div className="flex h-full flex-col">
        <Toolbar
          search={search}
          onSearchChange={setSearch}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
        />
        <div className="min-h-0 flex-1 pt-3">
          <Grid key={params.id} grid={grid} dataSource={dataSource} />
        </div>
      </div>
    </WithLocalStorage>
  );
}

function cutoffIsoFor(range: DateRangePreset): string | null {
  if (range === 'all') return null;
  const now = Date.now();
  const ms =
    range === '24h'
      ? 24 * 60 * 60 * 1000
      : range === '7d'
        ? 7 * 24 * 60 * 60 * 1000
        : 30 * 24 * 60 * 60 * 1000;
  return new Date(now - ms).toISOString();
}
